// src/components/RPA/buttons/UploadBankReconciliationButton.js
import React, { useState, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

/**
 * UploadBankReconciliationButton
 * 
 * Two-step workflow:
 * 1. Download pre-filled CSV template with bank balance
 * 2. User fills in reconciling items in Excel
 * 3. Upload completed template for AI analysis
 * 
 * Features:
 * - Auto-populates bank balance from uploaded bank statements
 * - Pre-fills book balance from trial balance
 * - Validates reconciliation math (bank + adjustments = book)
 * - Identifies journal entries needed
 */
const UploadBankReconciliationButton = ({ docId, onDone, disabledHint, doc }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reconciliationSummary, setReconciliationSummary] = useState(null);
  const fileInputRef = useRef(null);

  // Extract data for template pre-population
  const bankBalance = doc?.balanceSheetAudit?.bankStatements?.[0]?.parsed?.closingBalance || 0;
  const bankAccountNumber = doc?.balanceSheetAudit?.bankStatements?.[0]?.accountNumber || '****0000';
  const bankAccountName = doc?.balanceSheetAudit?.bankStatements?.[0]?.accountName || 'Unknown Account';
  const statementDate = doc?.balanceSheetAudit?.bankStatements?.[0]?.statementPeriod?.endDate || new Date().toISOString().split('T')[0];
  
  // Try to get book balance from trial balance cash accounts
  let bookBalance = 0;
  if (doc?.balanceSheetAudit?.trialBalance?.data) {
    const trialBalanceData = typeof doc.balanceSheetAudit.trialBalance.data === 'string'
      ? JSON.parse(doc.balanceSheetAudit.trialBalance.data)
      : doc.balanceSheetAudit.trialBalance.data;
    
    // Find cash account in trial balance
    const cashAccount = trialBalanceData.find(acc => 
      (acc.Account || acc.account || '').toLowerCase().includes('cash')
    );
    
    if (cashAccount) {
      bookBalance = parseFloat(cashAccount.Debit || cashAccount.debit || cashAccount.Balance || cashAccount.balance || 0);
    }
  }

  /**
   * Generate and download CSV template
   * Pre-fills known values (bank balance, book balance)
   */
  const handleDownloadTemplate = () => {
    console.log('[BANK_RECON] Generating template with:', {
      bankBalance,
      bookBalance,
      bankAccountNumber,
      statementDate
    });

    // CSV template structure
    const template = [
      // Header row
      ['Item', 'Amount', 'Description', 'Journal_Entry_Needed'],
      
      // Pre-filled rows
      ['Bank Balance', bankBalance, `Per ${bankAccountName} statement as of ${statementDate}`, 'No'],
      ['Book Balance', bookBalance, 'Per general ledger (verify from trial balance)', 'No'],
      
      // Empty rows for user to fill
      ['Deposits in Transit', '', 'Deposits received after bank cutoff date', 'No'],
      ['Outstanding Checks', '', 'Checks issued but not yet cleared (enter as NEGATIVE)', 'No'],
      ['Bank Fees', '', 'Service charges not yet recorded in books (enter as NEGATIVE)', 'Yes'],
      ['Interest Earned', '', 'Interest income not yet recorded in books', 'Yes'],
      ['NSF Checks', '', 'Checks returned for insufficient funds (enter as NEGATIVE)', 'Yes'],
      ['Wire Transfer Fees', '', 'Wire fees not recorded (enter as NEGATIVE)', 'Yes'],
      ['Other Adjustments', '', 'Other reconciling items (describe clearly)', ''],
      
      // Instructions section
      ['', '', '', ''],
      ['INSTRUCTIONS:', '', '', ''],
      ['1. Review pre-filled Bank Balance and Book Balance', '', '', ''],
      ['2. Enter amounts for reconciling items (leave blank if $0)', '', '', ''],
      ['3. Enter NEGATIVE amounts for: Outstanding Checks, Bank Fees, NSF Checks', '', '', ''],
      ['4. Enter POSITIVE amounts for: Deposits in Transit, Interest Earned', '', '', ''],
      ['5. Add additional rows if needed (copy format above)', '', '', ''],
      ['6. Save this file and upload it using the Upload button above', '', '', ''],
      ['', '', '', ''],
      ['FORMULA CHECK:', '', '', ''],
      ['Bank Balance + Deposits in Transit - Outstanding Checks + Interest - Fees = Book Balance', '', '', ''],
      ['', '', '', ''],
      [`Example: $${bankBalance.toLocaleString()} + $25,000 - $18,500 + $1,200 - $350 = $${bookBalance.toLocaleString()}`, '', '', '']
    ];

    // Convert to CSV
    const csvContent = template.map(row => 
      row.map(cell => {
        // Escape cells containing commas or quotes
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(',')
    ).join('\n');

    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Bank_Reconciliation_${bankAccountNumber}_${statementDate.replace(/\//g, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log('[BANK_RECON] ✅ Template downloaded');
  };

  /**
   * Handle completed template upload
   */
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file (exported from Excel or saved as CSV)');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setReconciliationSummary(null);

    try {
      const formData = new FormData();
      formData.append('reconciliation', file);

      console.log('[BANK_RECON] Uploading reconciliation for doc', docId);

      const res = await axios.post(
        `${API_URL}/admin/rpa/docs/${docId}/bank-reconciliation/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          timeout: 60000
        }
      );

      console.log('[BANK_RECON] Upload response:', res.data);

      if (res.data.success) {
        const summary = res.data.summary || {};
        
        setSuccess(
          summary.isReconciled
            ? `✅ Bank reconciliation BALANCED! Difference: $${Math.abs(summary.difference || 0).toLocaleString()}`
            : `⚠️ Bank reconciliation uploaded but does NOT balance. Difference: $${Math.abs(summary.difference || 0).toLocaleString()}`
        );
        
        setReconciliationSummary(summary);

        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Call onDone callback
        if (onDone) {
          setTimeout(() => {
            onDone();
          }, 2000);
        }
      } else {
        setError(res.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('[BANK_RECON] Upload error:', err);
      const errMsg = err.response?.data?.message || err.message || 'Upload failed';
      setError(`Failed to upload: ${errMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const disabled = uploading || !!disabledHint;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Step 1: Download Template */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={handleDownloadTemplate}
          disabled={disabled}
          style={{
            background: disabled ? '#e5e7eb' : '#3b82f6',
            color: disabled ? '#9ca3af' : 'white',
            border: 'none',
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            transition: 'all 0.2s'
          }}
        >
          📥 Download Reconciliation Template
        </button>

        <span style={{ fontSize: 11, color: '#6b7280' }}>
          (Step 1: Fill in Excel)
        </span>
      </div>

      {/* Step 2: Upload Completed Template */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label
          style={{
            background: disabled ? '#e5e7eb' : '#10b981',
            color: disabled ? '#9ca3af' : 'white',
            border: 'none',
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            transition: 'all 0.2s'
          }}
        >
          {uploading ? '⏳ Uploading...' : '📤 Upload Completed Reconciliation'}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleUpload}
            disabled={disabled}
            style={{ display: 'none' }}
          />
        </label>

        <span style={{ fontSize: 11, color: '#6b7280' }}>
          (Step 2: Upload CSV)
        </span>

        {disabledHint && (
          <span
            style={{
              fontSize: 11,
              color: '#f59e0b',
              fontStyle: 'italic'
            }}
          >
            ⚠️ {disabledHint}
          </span>
        )}
      </div>

      {/* Template Info Card */}
      <div
        style={{
          padding: 10,
          background: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: 6,
          fontSize: 11,
          color: '#0369a1'
        }}
      >
        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>📊 Template Pre-filled Data:</div>
        <div>• Bank Balance: ${bankBalance.toLocaleString()} (from {bankAccountName})</div>
        <div>• Book Balance: ${bookBalance.toLocaleString()} (from trial balance)</div>
        <div>• Statement Date: {statementDate}</div>
        <div style={{ marginTop: 6, fontSize: 10, color: '#0284c7' }}>
          Download template, fill in reconciling items in Excel, save as CSV, then upload.
        </div>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div
          style={{
            padding: 8,
            background: '#dbeafe',
            border: '1px solid #3b82f6',
            borderRadius: 6,
            fontSize: 12,
            color: '#1e40af'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 16,
                height: 16,
                border: '2px solid #3b82f6',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
            <span>Parsing and validating bank reconciliation...</span>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div
          style={{
            padding: 10,
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 6,
            fontSize: 12,
            color: '#991b1b'
          }}
        >
          ❌ {error}
        </div>
      )}

      {/* Success Display */}
      {success && (
        <div
          style={{
            padding: 10,
            background: reconciliationSummary?.isReconciled ? '#f0fdf4' : '#fef3c7',
            border: `1px solid ${reconciliationSummary?.isReconciled ? '#86efac' : '#fbbf24'}`,
            borderRadius: 6,
            fontSize: 12,
            color: reconciliationSummary?.isReconciled ? '#15803d' : '#92400e'
          }}
        >
          {success}
        </div>
      )}

      {/* Reconciliation Summary */}
      {reconciliationSummary && (
        <div
          style={{
            padding: 12,
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            fontSize: 12,
            marginTop: 4
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#111827' }}>
            📋 Reconciliation Summary
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Bank Balance</div>
              <div style={{ fontSize: 14, fontWeight: 'bold' }}>
                ${reconciliationSummary.bankBalance?.toLocaleString() || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Book Balance</div>
              <div style={{ fontSize: 14, fontWeight: 'bold' }}>
                ${reconciliationSummary.bookBalance?.toLocaleString() || 0}
              </div>
            </div>
          </div>

          {reconciliationSummary.reconciliationItems && reconciliationSummary.reconciliationItems.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 4 }}>Reconciling Items:</div>
              {reconciliationSummary.reconciliationItems.map((item, idx) => (
                <div key={idx} style={{ fontSize: 11, marginLeft: 12, marginBottom: 2 }}>
                  • {item.item}: ${item.amount?.toLocaleString() || 0}
                  {item.needsJournalEntry && <span style={{ color: '#f59e0b' }}> (JE needed)</span>}
                </div>
              ))}
            </div>
          )}

          <div style={{ 
            marginTop: 8, 
            paddingTop: 8, 
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Adjusted Bank Balance</div>
              <div style={{ fontSize: 14, fontWeight: 'bold' }}>
                ${reconciliationSummary.adjustedBankBalance?.toLocaleString() || 0}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Difference</div>
              <div style={{ 
                fontSize: 14, 
                fontWeight: 'bold',
                color: reconciliationSummary.isReconciled ? '#059669' : '#dc2626'
              }}>
                ${Math.abs(reconciliationSummary.difference || 0).toLocaleString()}
              </div>
            </div>
          </div>

          {reconciliationSummary.journalEntriesNeeded && reconciliationSummary.journalEntriesNeeded.length > 0 && (
            <div style={{ 
              marginTop: 8, 
              padding: 8, 
              background: '#fef3c7', 
              border: '1px solid #f59e0b',
              borderRadius: 4
            }}>
              <div style={{ fontSize: 11, fontWeight: 'bold', color: '#92400e', marginBottom: 4 }}>
                ⚡ Journal Entries Needed:
              </div>
              {reconciliationSummary.journalEntriesNeeded.map((je, idx) => (
                <div key={idx} style={{ fontSize: 10, color: '#92400e', marginLeft: 8 }}>
                  {idx + 1}. {je.description}: ${Math.abs(je.amount || 0).toLocaleString()}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default UploadBankReconciliationButton;